import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { PageHeader } from "@/components/admin/shared/PageHeader";
import {
  PennField,
  PennInput,
  PennCheckbox,
  PennButton,
} from "@/components/ui";
import { smtpSchema, type SmtpFormData } from "@/types/admin/smtp.types";
import {
  useEmailSettings,
  useSendTestEmail,
  useUpsertEmailSettings,
} from "@/hooks/useEmailSettings";
import type { SendTestResult } from "@/api/emailSettings";

const blankForm: SmtpFormData = {
  enabled: false,
  fromEmail: "",
  fromName: "Installation Systems Configurator",
  replyToEmail: "",
  testRecipient: "",
};

const apiErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === "object" && "response" in err) {
    const data = (
      err as { response?: { data?: { error?: { message?: string } } } }
    ).response?.data;
    if (data?.error?.message) return data.error.message;
  }
  return err instanceof Error ? err.message : fallback;
};

type Banner = { kind: "success" | "error"; text: string } | null;

export const SmtpPage = () => {
  const { data, isFetching, refetch } = useEmailSettings();
  const upsert = useUpsertEmailSettings();
  const sendTest = useSendTestEmail();

  const [banner, setBanner] = useState<Banner>(null);

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<SmtpFormData>({
    resolver: zodResolver(smtpSchema),
    defaultValues: blankForm,
  });

  // Pre-fill the form whenever the singleton lands or refreshes.
  useEffect(() => {
    if (!data) return;
    if (!data.configured || !data.settings) {
      reset(blankForm);
      return;
    }
    const s = data.settings;
    reset({
      enabled: s.enabled,
      fromEmail: s.fromEmail ?? "",
      fromName: s.fromName ?? "",
      replyToEmail: s.replyToEmail ?? "",
      testRecipient: s.testRecipient ?? "",
    });
  }, [data, reset]);

  const onSubmit = (formData: SmtpFormData) => {
    setBanner(null);
    upsert.mutate(
      {
        enabled: formData.enabled,
        fromEmail: formData.fromEmail.trim(),
        fromName: formData.fromName.trim() || null,
        replyToEmail: formData.replyToEmail?.trim() || null,
        testRecipient: formData.testRecipient?.trim() || null,
      },
      {
        onSuccess: () =>
          setBanner({ kind: "success", text: "Email settings saved." }),
        onError: (err) =>
          setBanner({
            kind: "error",
            text: apiErrorMessage(err, "Failed to save email settings."),
          }),
      },
    );
  };

  const onReload = () => {
    setBanner(null);
    void refetch();
  };

  const onSendTest = () => {
    setBanner(null);
    const recipient =
      getValues("testRecipient")?.trim() || getValues("fromEmail").trim();
    if (!recipient) {
      setBanner({
        kind: "error",
        text: "Enter a Test Recipient (or From Email) before sending.",
      });
      return;
    }
    sendTest.mutate(recipient, {
      onSuccess: (result: SendTestResult) => {
        if (result.ok) {
          setBanner({
            kind: "success",
            text: `Test email sent. messageId: ${result.messageId}`,
          });
        } else {
          setBanner({
            kind: "error",
            text: `[${result.stage}/${result.code}] ${result.message}`,
          });
        }
      },
      onError: (err) =>
        setBanner({
          kind: "error",
          text: apiErrorMessage(err, "Failed to send test email."),
        }),
    });
  };

  const isSaving = upsert.isPending || isSubmitting;

  return (
    <>
      <PageHeader
        title="Email"
        subtitle="Configure task notification emails and send a test."
        action={
          <div className="flex flex-wrap gap-2">
            <PennButton
              type="button"
              variant="secondary"
              size="sm"
              onClick={onReload}
              disabled={isFetching}
            >
              {isFetching ? "Reloading…" : "Reload"}
            </PennButton>
            <PennButton
              type="button"
              variant="secondary"
              size="sm"
              onClick={onSendTest}
              disabled={sendTest.isPending}
            >
              {sendTest.isPending ? "Sending…" : "Send Test"}
            </PennButton>
            <PennButton
              type="button"
              variant="primary"
              size="sm"
              onClick={handleSubmit(onSubmit)}
              disabled={isSaving}
            >
              {isSaving ? "Saving…" : "Save"}
            </PennButton>
          </div>
        }
      />

      {banner && (
        <div
          className={`mb-4 rounded-lg border px-4 py-3 text-body ${
            banner.kind === "success"
              ? "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950/40 dark:text-green-400"
              : "border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-400"
          }`}
        >
          {banner.text}
        </div>
      )}

      <form className="space-y-5 max-w-4xl" onSubmit={handleSubmit(onSubmit)}>
        {/* Toggle row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-4 shadow-sm">
            <p className="uppercase text-blue_grey4 mb-2 text-small-medium">
              Enabled
            </p>
            <label className="flex items-center gap-2 text-blue_grey cursor-pointer text-body">
              <PennCheckbox {...register("enabled")} />
              Send task emails whenever a new RFQ task is created.
            </label>
          </div>
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-4 shadow-sm">
            <p className="uppercase text-blue_grey4 mb-2 text-small-medium">
              Secure transport
            </p>
            <label className="flex items-center gap-2 text-blue_grey4 cursor-not-allowed text-body">
              <PennCheckbox checked readOnly disabled />
              TLS — managed by the internal outbound-emailer (always on).
            </label>
          </div>
        </div>

        {/* Fields grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <PennField
            label="From Email"
            htmlFor="fromEmail"
            required
            error={errors.fromEmail?.message}
          >
            <PennInput
              id="fromEmail"
              type="email"
              placeholder="notifications@example.com"
              error={!!errors.fromEmail}
              {...register("fromEmail")}
            />
          </PennField>
          <PennField
            label="From Name"
            htmlFor="fromName"
            required
            error={errors.fromName?.message}
          >
            <PennInput
              id="fromName"
              placeholder="Installation Systems Configurator"
              error={!!errors.fromName}
              {...register("fromName")}
            />
          </PennField>
          <PennField
            label="Test Recipient"
            htmlFor="testRecipient"
            error={errors.testRecipient?.message}
          >
            <PennInput
              id="testRecipient"
              placeholder="ops@example.com"
              error={!!errors.testRecipient}
              {...register("testRecipient")}
            />
          </PennField>
        </div>

        {/* Delivery — fixed internal outbound-emailer; no admin-configurable
            transport since 2026-07-01 (docs/spec/outbound-emailer-migration.md). */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-4 space-y-4 shadow-sm">
          <p className="uppercase text-blue_grey4 text-small-medium">
            Delivery
          </p>
          <PennField label="Default From Address">
            <PennInput
              value={data?.env?.defaultFromAddress ?? ""}
              readOnly
              disabled
              placeholder="no-reply@example.com"
            />
          </PennField>
          <p className="text-blue_grey4 text-small">
            Email is delivered by the internal outbound-emailer (managed by ops)
            — there is nothing to configure here. After changing the settings
            above, click <strong>Save</strong>, then <strong>Send Test</strong>{" "}
            to confirm delivery (a green result = healthy). Default From Address
            is the allowlisted sender, managed via environment configuration —
            read-only.
          </p>
        </div>

        <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 bg-gray-50 dark:bg-gray-800 text-blue_grey2 text-body">
          Task emails include the RFQ snapshot, configured machine options,
          applications, and ZIP attachments for each saved application in the
          request that has files on disk.
        </div>
      </form>
    </>
  );
};
